import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { MarginRisk } from '@swyftx/margin/build/types';
import { AT_RISK_THRESHOLD, WARNING_THRESHOLD, MONITOR_THRESHOLD } from '@swyftx/margin/build/utils';

import { Big } from '@shared/safe-big';
import { cn } from '@shared/utils/lib/ui';

import { useMarginAccount } from 'src/lib/margin/hooks/account';

import { MarginAccountHealthBarThresholdItem } from './MarginAccountHealthBarThresholdItem';

type Props = {
  maxIncrement: number;
  stepIncrement: number;
  customRiskRating?: MarginRisk;
};

const MarginAccountHealthBarThresholds: React.FC<Props> = ({ maxIncrement, stepIncrement, customRiskRating }) => {
  const { riskRating: accountRiskRating } = useMarginAccount();
  const { t } = useTranslation('margin');

  const riskRating = useMemo(() => customRiskRating ?? accountRiskRating, [accountRiskRating, customRiskRating]);

  return (
    <FlexLayout alignItems='center' justifyContent='space-between' spacing={4} className='w-full'>
      <MarginAccountHealthBarThresholdItem
        title={t(`accountHealth.riskRating.status.${MarginRisk.StopOut}`)}
        className={cn('bg-color-background-error', riskRating === MarginRisk.StopOut ? '' : 'opacity-50')}
        threshold={AT_RISK_THRESHOLD}
        maxIncrement={maxIncrement}
        color={riskRating === MarginRisk.StopOut ? 'primary' : 'secondary'}
        weight={riskRating === MarginRisk.StopOut ? 'bold' : 'emphasis'}
        tooltip={t(`accountHealth.riskRating.marginLevel.${MarginRisk.StopOut}`, { upperThreshold: AT_RISK_THRESHOLD })}
      />
      <MarginAccountHealthBarThresholdItem
        title={t(`accountHealth.riskRating.status.${MarginRisk.AtRisk}`)}
        className={cn('bg-color-background-error', riskRating === MarginRisk.AtRisk ? '' : 'opacity-50')}
        threshold={Big(WARNING_THRESHOLD).minus(AT_RISK_THRESHOLD).toNumber()}
        maxIncrement={maxIncrement}
        color={riskRating === MarginRisk.AtRisk ? 'primary' : 'secondary'}
        weight={riskRating === MarginRisk.AtRisk ? 'bold' : 'emphasis'}
        tooltip={t(`accountHealth.riskRating.marginLevel.${MarginRisk.AtRisk}`, {
          lowerThreshold: AT_RISK_THRESHOLD,
          upperThreshold: WARNING_THRESHOLD,
        })}
      />
      <MarginAccountHealthBarThresholdItem
        title={t(`accountHealth.riskRating.status.${MarginRisk.Warning}`)}
        className={cn('bg-color-background-warning', riskRating === MarginRisk.Warning ? '' : 'opacity-50')}
        threshold={Big(MONITOR_THRESHOLD).minus(WARNING_THRESHOLD).toNumber()}
        maxIncrement={maxIncrement}
        color={riskRating === MarginRisk.Warning ? 'primary' : 'secondary'}
        weight={riskRating === MarginRisk.Warning ? 'bold' : 'emphasis'}
        tooltip={t(`accountHealth.riskRating.marginLevel.${MarginRisk.Warning}`, {
          lowerThreshold: WARNING_THRESHOLD,
          upperThreshold: MONITOR_THRESHOLD,
        })}
      />
      <MarginAccountHealthBarThresholdItem
        title={t(`accountHealth.riskRating.status.${MarginRisk.Monitor}`)}
        className={cn('bg-color-background-info', riskRating === MarginRisk.Monitor ? '' : 'opacity-50')}
        threshold={Big(maxIncrement + stepIncrement / 2)
          .minus(MONITOR_THRESHOLD)
          .toNumber()}
        maxIncrement={maxIncrement}
        color={riskRating === MarginRisk.Monitor ? 'primary' : 'secondary'}
        weight={riskRating === MarginRisk.Monitor ? 'bold' : 'emphasis'}
        tooltip={t(`accountHealth.riskRating.marginLevel.${MarginRisk.Monitor}`, {
          lowerThreshold: MONITOR_THRESHOLD,
        })}
      />
    </FlexLayout>
  );
};

export { MarginAccountHealthBarThresholds };
